import { Component, effect, inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AbstractDynamicDialogComponent } from '../../../components/dynamic-dialog/abstract-dynamic-dialog.component';
import { getErrorMessage } from '../../../consts/form-field-errors';
import { EMAIL_PATTERN_STRING } from '../../../consts/patterns.const';
import { COUNTRIES, STATES } from '../../../model/states.model';
import { abnValidator, duplicateAbnValidator } from '../../../validators/abn.validator';
import { RepairerSitesStore } from '../repairer-sites/repairer-sites-store.component';
import { SetDashboardDayComponent } from '../set-dashboard-day/set-dashboard-day.component';
import { exactNumberOfCharactersValidator } from '../../../validators/exact-number-of-characters.validator';
import { PhoneValidator } from '../../../validators/phone.validator';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TrimInputDirective } from '../../../directives/trim-input.directive';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';
import { MatSelect, MatOption } from '@angular/material/select';
import { NgClass } from '@angular/common';
import { NumbersOnlyDirective } from '../../../directives/numbers-only.directive';
import { AlphanumericDirective } from '../../../directives/alphanumeric.directive';

@Component({
  selector: 'app-add-factoring-company',
  templateUrl: './add-factoring-company.component.html',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    TrimInputDirective,
    BrowserAutoFillDisabledDirective,
    MatError,
    MatSelect,
    MatOption,
    NgClass,
    NumbersOnlyDirective,
    AlphanumericDirective
  ]
})
export class AddFactoringCompanyComponent extends AbstractDynamicDialogComponent<any> implements OnInit, OnDestroy {
  protected readonly states = STATES;
  protected readonly countries = COUNTRIES;
  protected readonly getErrorMessage = getErrorMessage;
  private unsubscribe = new Subject<void>();

  #dialogRef = inject(MatDialogRef<SetDashboardDayComponent>);
  #fb = inject(UntypedFormBuilder);
  repairerSitesStore = inject(RepairerSitesStore);

  constructor() {
    super();

    effect(() => {
      this.repairerSitesStore.abnUpdateErrors() && this.abn.setErrors(this.repairerSitesStore.abnUpdateErrors());
    });
  }

  get email() {
    return this.form?.controls?.email;
  }

  get phone() {
    return this.form?.controls?.phone;
  }

  get abn() {
    return this.form?.controls?.abn;
  }

  get companyName() {
    return this.form?.controls?.companyName;
  }

  get country() {
    return this.form?.controls?.country;
  }

  get bsb() {
    return this.form?.controls?.bsb;
  }

  get accountName() {
    return this.form?.controls?.accountName;
  }

  get accountNumber() {
    return this.form?.controls?.accountNumber;
  }

  ngOnInit(): void {
    this.dynamicDialogService.dialogRefSignal.set(this.#dialogRef);
    const {
      companyName = null,
      abn = null,
      addressLine1 = null,
      addressLine2 = null,
      email = null,
      phone = null,
      suburb = null,
      country = null,
      state = null,
      city = null,
      postCode = null,
      bsb = null,
      accountName = null,
      accountNumber = null
    } = this.data?.factoringCompany || {};
    this.form = this.#fb.group({
      companyName: [companyName, [Validators.required]],
      abn: [
        abn,
        this.data?.factoringCompany?.country === 'Australia'
          ? [Validators.required, abnValidator, duplicateAbnValidator(this.data?.existingAbns || [], abn)]
          : [Validators.required]
      ],
      addressLine1,
      addressLine2,
      email: [email, [Validators.required, Validators.pattern(EMAIL_PATTERN_STRING)]],
      phone: [phone, PhoneValidator],
      suburb,
      country: [country, Validators.required],
      state,
      city,
      postCode,
      bsb: [bsb, [Validators.required, exactNumberOfCharactersValidator(6)]],
      accountName: [accountName, Validators.required],
      accountNumber: [accountNumber, [Validators.required, Validators.minLength(6)]]
    });

    this.phone.markAsTouched();

    this.country.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe({
      next: (country: string) => {
        this.abn.setValidators(
          country === 'Australia'
            ? [Validators.required, abnValidator, duplicateAbnValidator(this.data?.existingAbns || [], abn)]
            : [Validators.required]
        );
        if (country === 'Australia') {
          this.adjustFormatOfAbn();
        }
        this.abn.updateValueAndValidity();
      }
    });
  }

  adjustFormatOfAbn() {
    this.abn.updateValueAndValidity();
    if (this.abn.valid && this.country.value === this.countries[0].stateName) {
      this.abn.patchValue(this.abn.value.replace(/\B(?=(\d{3})+(?!\d))/g, ' '));
    }
  }

  clearStateAndCity(country: string) {
    country === 'Australia' ? this.form.controls.state.setValue('') : this.form.controls.city.setValue('');
  }

  saveOrExecuteAction(): void {
    if (this.form.valid) {
      this.disabled.set(true);
      this.repairerSitesStore.addOrUpdateFactoringCompany({
        id: this.data?.factoringCompany?.id,
        factoringCompany: this.form.getRawValue()
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
